import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Search as SearchIcon, Eye, EyeOff, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = {
  categories: { label: "Catégories", endpoint: "/admin/meta/categories" },
  cities: { label: "Villes", endpoint: "/admin/meta/cities" },
  markets: { label: "Marchés", endpoint: "/admin/meta/markets" },
};

export default function AdminMetadata() {
  const [type, setType] = useState("categories");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = () => api.get(TYPES[type].endpoint).then(r => setItems(r.data || []));
  useEffect(() => { load(); setExpanded({}); }, [type]);

  const toggleActive = async (it) => {
    await api.patch(`${TYPES[type].endpoint}/${it.id}`, { active: !it.active });
    load();
  };
  const del = async (it) => {
    if (!window.confirm(`Supprimer « ${it.name || it.label} » ?`)) return;
    try {
      await api.delete(`${TYPES[type].endpoint}/${it.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async (payload) => {
    try {
      if (payload.id) await api.patch(`${TYPES[type].endpoint}/${payload.id}`, payload);
      else await api.post(TYPES[type].endpoint, payload);
      toast.success("Enregistré");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Phase 12: reorder by re-numbering all visible items 0..N
  const reorder = async (list, from, to) => {
    if (from === to || to < 0 || to >= list.length) return;
    const newList = [...list];
    const [moved] = newList.splice(from, 1);
    newList.splice(to, 0, moved);
    try {
      // Patch only items whose order actually changed
      await Promise.all(
        newList
          .map((it, i) => (it.order !== i ? api.patch(`${TYPES[type].endpoint}/${it.id}`, { order: i }) : null))
          .filter(Boolean),
      );
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const matchesQuery = (it) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (it.name || it.label || it.slug || "").toLowerCase().includes(s);
  };

  // For categories: split into parents and subs (grouped by parent_slug)
  const isCats = type === "categories";
  const parents = isCats ? items.filter(i => !i.parent_slug) : [];
  const subsByParent = {};
  if (isCats) {
    items.filter(i => i.parent_slug).forEach(i => {
      subsByParent[i.parent_slug] = subsByParent[i.parent_slug] || [];
      subsByParent[i.parent_slug].push(i);
    });
  }
  const filteredParents = parents.filter(matchesQuery);
  const filteredItems = !isCats ? items.filter(matchesQuery) : [];

  const newItem = (parentSlug = null) => setEditing({ parent_slug: parentSlug });

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        {Object.entries(TYPES).map(([k, t]) => (
          <button
            key={k}
            data-testid={`meta-tab-${k}`}
            onClick={() => setType(k)}
            className={`h-9 px-3 rounded-lg text-xs font-bold transition border ${type === k ? "bg-[#E65100] text-white border-[#E65100]" : "bg-white dark:bg-[#1A1918] border-[#E8E6E1] text-[#1A1918]"}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#73706B]" />
          <Input data-testid="meta-search" className="h-10 pl-9 rounded-xl" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button onClick={() => newItem(null)} className="h-10 rounded-xl bg-[#E65100] hover:bg-[#C44100]" data-testid="meta-new-btn">
          <Plus className="h-4 w-4 mr-1" /> Nouveau
        </Button>
      </div>

      {isCats ? (
        <div className="mt-3 space-y-2">
          {filteredParents.map((it, idx) => {
            const subs = subsByParent[it.slug] || [];
            const isOpen = expanded[it.id];
            return (
              <div key={it.id} className={`rounded-xl border bg-white ${it.active === false ? "opacity-60 border-dashed" : "border-[#E8E6E1]"}`} data-testid={`meta-row-${it.id}`}>
                <div className="p-3 flex items-center gap-2">
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button type="button" onClick={() => reorder(filteredParents, idx, idx - 1)} disabled={idx === 0} className="w-5 h-5 rounded bg-[#F3F1EC] hover:bg-[#E65100]/10 disabled:opacity-30 flex items-center justify-center" data-testid={`meta-up-${it.id}`} title="Monter"><ArrowUp className="h-3 w-3" /></button>
                    <button type="button" onClick={() => reorder(filteredParents, idx, idx + 1)} disabled={idx === filteredParents.length - 1} className="w-5 h-5 rounded bg-[#F3F1EC] hover:bg-[#E65100]/10 disabled:opacity-30 flex items-center justify-center" data-testid={`meta-down-${it.id}`} title="Descendre"><ArrowDown className="h-3 w-3" /></button>
                  </div>
                  <button type="button" onClick={() => setExpanded(e => ({ ...e, [it.id]: !e[it.id] }))} className="shrink-0 w-7 h-7 rounded-lg bg-[#F3F1EC] hover:bg-[#E65100]/10 flex items-center justify-center" aria-label="Développer" data-testid={`meta-expand-${it.id}`}>
                    <ChevronRight className={`h-4 w-4 transition ${isOpen ? "rotate-90" : ""}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{it.label} <span className="text-[11px] font-normal text-[#73706B]">· {subs.length} sous-cat.</span></div>
                    <div className="text-xs text-[#73706B]">slug: {it.slug} · icône: {it.icon}{it.active === false ? " · Désactivé" : ""}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(it)} data-testid={`meta-toggle-${it.id}`}>
                    {it.active === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`meta-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(it)} className="text-[#D32F2F]" data-testid={`meta-del-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>

                {isOpen && (
                  <div className="px-3 pb-3 space-y-1.5 border-t border-[#E8E6E1] pt-3 bg-[#FFF8F3]/40">
                    {subs.filter(matchesQuery).map(s => (
                      <div key={s.id} className={`p-2 rounded-lg border bg-white flex items-center gap-2 ${s.active === false ? "opacity-60 border-dashed" : "border-[#E8E6E1]"}`} data-testid={`meta-sub-row-${s.id}`}>
                        <div className="w-1 h-8 rounded-full bg-[#E65100]/30 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{s.label}</div>
                          <div className="text-[11px] text-[#73706B]">slug: {s.slug}{s.active === false ? " · Désactivé" : ""}</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(s)} data-testid={`meta-toggle-${s.id}`}>
                          {s.active === false ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`meta-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => del(s)} className="text-[#D32F2F]" data-testid={`meta-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                    <Button variant="outline" className="w-full h-9 rounded-lg border-dashed" onClick={() => newItem(it.slug)} data-testid={`meta-new-sub-${it.slug}`}>
                      <Plus className="h-4 w-4 mr-1" /> Ajouter une sous-catégorie
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {!filteredParents.length && <div className="py-8 text-center text-sm text-[#73706B]">Aucune catégorie</div>}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {filteredItems.map((it, idx) => (
            <div key={it.id} data-testid={`meta-row-${it.id}`} className={`p-3 rounded-xl border bg-white flex items-center gap-2 ${it.active === false ? "opacity-60 border-dashed" : "border-[#E8E6E1]"}`}>
              <div className="flex flex-col gap-0.5 shrink-0">
                <button type="button" onClick={() => reorder(filteredItems, idx, idx - 1)} disabled={idx === 0} className="w-5 h-5 rounded bg-[#F3F1EC] hover:bg-[#E65100]/10 disabled:opacity-30 flex items-center justify-center" data-testid={`meta-up-${it.id}`} title="Monter"><ArrowUp className="h-3 w-3" /></button>
                <button type="button" onClick={() => reorder(filteredItems, idx, idx + 1)} disabled={idx === filteredItems.length - 1} className="w-5 h-5 rounded bg-[#F3F1EC] hover:bg-[#E65100]/10 disabled:opacity-30 flex items-center justify-center" data-testid={`meta-down-${it.id}`} title="Descendre"><ArrowDown className="h-3 w-3" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{it.label || it.name}</div>
                <div className="text-xs text-[#73706B]">{it.active === false ? "Désactivé" : "Actif"}{it.city ? ` · ${it.city}` : ""}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => toggleActive(it)} data-testid={`meta-toggle-${it.id}`}>
                {it.active === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`meta-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => del(it)} className="text-[#D32F2F]" data-testid={`meta-del-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {!filteredItems.length && <div className="py-8 text-center text-sm text-[#73706B]">Aucun élément</div>}
        </div>
      )}

      <EditDialog type={type} item={editing} parents={parents} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function EditDialog({ type, item, parents, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [cities, setCities] = useState([]);
  useEffect(() => { setForm(item || {}); }, [item]);
  useEffect(() => {
    if (type === "markets") api.get("/admin/meta/cities").then(r => setCities((r.data || []).filter(c => c.active !== false)));
  }, [type]);
  if (item === null) return null;
  const isCat = type === "categories";
  const isMarket = type === "markets";
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isSub = !!form.parent_slug;

  return (
    <Dialog open={item !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {form.id ? "Modifier" : "Créer"} {isCat ? (isSub ? "sous-catégorie" : "catégorie") : type === "cities" ? "ville" : "marché"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isCat && (
            <div>
              <Label>Parent (laisser vide pour une catégorie principale)</Label>
              <Select value={form.parent_slug || "__none__"} onValueChange={(v) => update("parent_slug", v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="meta-input-parent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Aucune (catégorie principale) —</SelectItem>
                  {parents.filter(p => p.id !== form.id).map(p => <SelectItem key={p.slug} value={p.slug}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {isCat ? (
            <>
              <div>
                <Label>Slug (ex: mode-homme)</Label>
                <Input data-testid="meta-input-slug" className="h-11 rounded-xl mt-1" value={form.slug || ""} onChange={(e) => update("slug", e.target.value)} />
              </div>
              <div>
                <Label>Libellé</Label>
                <Input data-testid="meta-input-label" className="h-11 rounded-xl mt-1" value={form.label || ""} onChange={(e) => update("label", e.target.value)} />
              </div>
              {!isSub && (
                <div>
                  <Label>Icône (lucide)</Label>
                  <Input data-testid="meta-input-icon" className="h-11 rounded-xl mt-1" value={form.icon || ""} onChange={(e) => update("icon", e.target.value)} placeholder="package" />
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <Label>Nom</Label>
                <Input data-testid="meta-input-name" className="h-11 rounded-xl mt-1" value={form.name || ""} onChange={(e) => update("name", e.target.value)} />
              </div>
              {isMarket && (
                <div>
                  <Label>Ville (rattachée à) *</Label>
                  <Select value={form.city || ""} onValueChange={(v) => update("city", v)}>
                    <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="meta-input-city"><SelectValue placeholder="Sélectionner une ville" /></SelectTrigger>
                    <SelectContent>
                      {form.city && !cities.find(c => c.name === form.city) && (
                        <SelectItem key={form.city} value={form.city}>{form.city}</SelectItem>
                      )}
                      {cities.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-[#73706B] mt-1">Un marché doit toujours être rattaché à une ville.</div>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="meta-save-btn" className="w-full h-11 rounded-xl bg-[#E65100] hover:bg-[#C44100]" onClick={() => onSave(form)}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
